/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ============================================
   SLIDE-PANEL NAVIGATION
   Manages panel transitions inside the mobile/drawer nav.
   Works with the snav__* structure in mobile-menu.liquid.
   ============================================ */
(function () {
  'use strict';

  var TRANSITION_MS = 280;

  function getTransitionMs(drawerEl) {
    var v = getComputedStyle(drawerEl).getPropertyValue('--snav-transition').trim();
    if (v) return parseInt(v, 10) || TRANSITION_MS;
    return TRANSITION_MS;
  }

  function SlidingNav(drawerEl) {
    this.drawer = drawerEl;
    this.viewport = drawerEl.querySelector('#snav-viewport');
    this.backBtn = drawerEl.querySelector('#snav-back-btn');
    this.titleEl = drawerEl.querySelector('#snav-panel-title');
    this.breadcrumbEl = drawerEl.querySelector('#snav-breadcrumb');
    this.stack = ['snav-root'];
    this.timers = [];
    this._init();
  }

  // Cancel all pending cleanup timers and immediately finalize any
  // in-progress panel transitions. Prevents race conditions when
  // the user navigates faster than the animation duration.
  SlidingNav.prototype._cancelTransitions = function () {
    this.timers.forEach(function (id) { clearTimeout(id); });
    this.timers = [];

    var self = this;
    var transitioning = this.drawer.querySelectorAll(
      '.snav__panel--exiting, .snav__panel--exiting-back, .snav__panel--entering'
    );
    transitioning.forEach(function (panel) {
      panel.classList.remove(
        'snav__panel--exiting',
        'snav__panel--exiting-back',
        'snav__panel--entering',
        'snav__panel--active'
      );
      // Only keep visible if it's currently in the navigation stack
      panel.hidden = self.stack.indexOf(panel.id) === -1;
    });
  };

  SlidingNav.prototype._init = function () {
    var self = this;

    this.drawer.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-open-panel]');
      if (openBtn) {
        e.preventDefault();
        self._openPanel(openBtn.dataset.openPanel, openBtn);
        return;
      }
      if (e.target.closest('#snav-back-btn')) {
        self._goBack();
      }
    });

    // Swipe right to go back
    var touchStartX = 0;
    var touchStartY = 0;
    this.viewport.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    this.viewport.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - touchStartX;
      var dy = e.changedTouches[0].clientY - touchStartY;
      if (dx > 60 && Math.abs(dy) < Math.abs(dx) * 0.6) {
        self._goBack();
      }
    }, { passive: true });

    // Reset when drawer closes (Focal toggles "open" attribute on mobile-navigation).
    // Store reference so _destroy() can disconnect it and prevent stale observers
    // from interfering after a section re-render creates a new instance.
    this._drawerObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName !== 'open') return;
        if (!self.drawer.hasAttribute('open')) {
          // Drawer closed → reset nav state after transition finishes
          setTimeout(function () { self._reset(); }, getTransitionMs(self.drawer) + 50);
        } else {
          // Drawer opened → ensure header UI is in sync (guards against missed resets)
          self._updateHeader();
        }
      });
    });
    this._drawerObserver.observe(this.drawer, { attributes: true });
  };

  // Fully tear down this instance: cancel animations and disconnect the drawer observer.
  // Must be called before creating a new SlidingNav on the same element.
  SlidingNav.prototype._destroy = function () {
    this._cancelTransitions();
    if (this._drawerObserver) {
      this._drawerObserver.disconnect();
      this._drawerObserver = null;
    }
  };

  SlidingNav.prototype._getPanel = function (id) {
    return this.drawer.querySelector('#' + id);
  };

  SlidingNav.prototype._currentPanel = function () {
    return this._getPanel(this.stack[this.stack.length - 1]);
  };

  SlidingNav.prototype._openPanel = function (targetId, triggerBtn) {
    this._cancelTransitions();

    var current = this._currentPanel();
    var next = this._getPanel(targetId);
    if (!next || !current) return;

    if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'true');

    // Reveal next off-screen right, then trigger transition
    next.hidden = false;
    next.classList.add('snav__panel--entering');
    next.offsetWidth; // force reflow

    current.classList.add('snav__panel--exiting');
    current.classList.remove('snav__panel--active');
    next.classList.remove('snav__panel--entering');
    next.classList.add('snav__panel--active');

    this.stack.push(targetId);
    this._updateHeader();

    var self = this;
    var ms = getTransitionMs(this.drawer);

    var focusTimer = setTimeout(function () {
      var firstEl = next.querySelector('a, button');
      if (firstEl) firstEl.focus();
    }, ms);

    var cleanupTimer = setTimeout(function () {
      current.classList.remove('snav__panel--exiting', 'snav__panel--active');
      current.hidden = true;
    }, ms);

    this.timers.push(focusTimer, cleanupTimer);
  };

  SlidingNav.prototype._goBack = function () {
    if (this.stack.length <= 1) return;
    this._cancelTransitions();

    var currentId = this.stack[this.stack.length - 1];
    var parentId = this.stack[this.stack.length - 2];
    var current = this._getPanel(currentId);
    var parent = this._getPanel(parentId);
    if (!parent) return;

    parent.hidden = false;
    parent.classList.add('snav__panel--active');

    current.classList.add('snav__panel--exiting-back');
    current.classList.remove('snav__panel--active');

    this.stack.pop();
    this._updateHeader();

    var self = this;
    var ms = getTransitionMs(this.drawer);

    var openBtn = parent.querySelector('[data-open-panel="' + currentId + '"]');
    if (openBtn) {
      openBtn.setAttribute('aria-expanded', 'false');
      var focusTimer = setTimeout(function () { openBtn.focus(); }, ms);
      this.timers.push(focusTimer);
    }

    var exitingPanel = current;
    var cleanupTimer = setTimeout(function () {
      exitingPanel.classList.remove('snav__panel--exiting-back', 'snav__panel--active');
      exitingPanel.hidden = true;
    }, ms);
    this.timers.push(cleanupTimer);
  };

  SlidingNav.prototype._updateHeader = function () {
    var depth = this.stack.length;
    var currentPanel = this._currentPanel();
    var panelTitle = (currentPanel && currentPanel.dataset.panelTitle) || 'Menu';

    this.titleEl.textContent = panelTitle;

    if (depth > 1) {
      this.backBtn.hidden = false;
    } else {
      this.titleEl.textContent = 'Menu';
      this.backBtn.hidden = true;
    }

    // Breadcrumb: show full ancestor path on Level 4+ only
    // (at depth 3 the back button already shows the parent — breadcrumb would duplicate it)
    if (this.breadcrumbEl) {
      if (depth >= 4) {
        var path = [];
        for (var i = 1; i < depth - 1; i++) {
          var ancestor = this._getPanel(this.stack[i]);
          if (ancestor && ancestor.dataset.panelTitle) {
            path.push(ancestor.dataset.panelTitle);
          }
        }
        this.breadcrumbEl.textContent = path.join(' › ');
        this.breadcrumbEl.hidden = false;
      } else {
        this.breadcrumbEl.textContent = '';
        this.breadcrumbEl.hidden = true;
      }
    }
  };

  // Jump directly to a panel by ID without animation (used by Theme Editor)
  SlidingNav.prototype._jumpToPanel = function (targetId) {
    var target = this._getPanel(targetId);
    if (!target) return;

    // Build path from root to target using data-parent-panel
    var path = [];
    var node = target;
    while (node && node.id !== 'snav-root') {
      path.unshift(node.id);
      var parentId = node.dataset.parentPanel;
      node = parentId ? this._getPanel(parentId) : null;
    }
    if (!path.length) return;

    // Reset without animation
    this._cancelTransitions();
    var self = this;
    this.drawer.querySelectorAll('.snav__panel').forEach(function (p) {
      p.classList.remove('snav__panel--active', 'snav__panel--entering', 'snav__panel--exiting', 'snav__panel--exiting-back');
      p.hidden = p.id !== 'snav-root';
    });

    // Show all panels in path, make last one active
    path.forEach(function (id) {
      var p = self._getPanel(id);
      if (p) { p.hidden = false; }
    });
    var finalPanel = this._getPanel(path[path.length - 1]);
    if (finalPanel) finalPanel.classList.add('snav__panel--active');

    this.stack = ['snav-root'].concat(path);
    this._updateHeader();
  };

  SlidingNav.prototype._reset = function () {
    this._cancelTransitions();
    var self = this;
    var panels = this.drawer.querySelectorAll('.snav__panel');

    panels.forEach(function (p) {
      p.classList.remove(
        'snav__panel--active',
        'snav__panel--entering',
        'snav__panel--exiting',
        'snav__panel--exiting-back'
      );
      if (p.id !== 'snav-root') {
        p.hidden = true;
      }
    });

    // Reset all aria-expanded
    var triggers = this.drawer.querySelectorAll('[data-open-panel]');
    triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

    var root = this._getPanel('snav-root');
    if (root) {
      root.hidden = false;                         // _openPanel hides root during navigation
      root.classList.add('snav__panel--active');
    }

    this.stack = ['snav-root'];
    this._updateHeader();
  };

  // ── Initialization ──────────────────────────────────────────────────────────
  // mobile-navigation uses append-body, so it may not be in the DOM at
  // DOMContentLoaded. Watch for it appearing in body.

  function tryInit() {
    document.querySelectorAll('mobile-navigation').forEach(function (el) {
      if (el.querySelector('#snav-viewport') && !el._snavInstance) {
        el._snavInstance = new SlidingNav(el);
      }
    });
  }

  var bodyObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.id === 'mobile-menu-drawer' || node.querySelector && node.querySelector('#snav-viewport')) {
          tryInit();
        }
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      tryInit();
      bodyObserver.observe(document.body, { childList: true, subtree: false });
    });
  } else {
    tryInit();
    bodyObserver.observe(document.body, { childList: true, subtree: false });
  }

  // Shopify Theme Editor live reloads.
  //
  // In design_mode, <mobile-navigation> has no append-body, so it stays inside the
  // section element and Shopify's re-render replaces it normally — exactly like Dawn.
  // We only need to destroy the stale instance and reinit the fresh element.
  document.addEventListener('shopify:section:unload', function () {
    document.querySelectorAll('mobile-navigation').forEach(function (el) {
      if (el._snavInstance) {
        el._snavInstance._destroy();
        el._snavInstance = null;
      }
    });
  });

  document.addEventListener('shopify:section:load', function () {
    tryInit();
  });

  // When a block is selected in the Theme Editor sidebar, auto-open the drawer
  // and navigate to the panel containing that block so it can be edited live.
  // Shopify outputs block.shopify_attributes as data-shopify-editor-block='{"id":"…","type":"…"}'
  // so we need a *-contains selector to match the blockId inside the JSON value.
  document.addEventListener('shopify:block:select', function (e) {
    var blockEl = document.querySelector('[data-shopify-editor-block*="' + e.detail.blockId + '"]');
    if (!blockEl) return;

    var panel = blockEl.closest('.snav__panel');

    // Open the mobile drawer (Focal listens to the "open" attribute)
    var drawerEl = blockEl.closest('mobile-navigation');
    if (drawerEl && !drawerEl.hasAttribute('open')) {
      drawerEl.setAttribute('open', '');
    }

    // If the block lives inside a non-root panel, navigate there
    if (panel && panel.id && panel.id !== 'snav-root' && drawerEl && drawerEl._snavInstance) {
      setTimeout(function () {
        drawerEl._snavInstance._jumpToPanel(panel.id);
      }, 50);
    }
  });

  document.addEventListener('shopify:block:deselect', function () {
    // Left intentionally empty — user may want the drawer to stay open while editing.
  });
}());

(function () {
  function bumpCartIcon() {
    var icons = document.querySelectorAll('.header__icon-wrapper[aria-controls="mini-cart"]');
    icons.forEach(function (el) {
      el.classList.remove('sm-cart-bump');
      void el.offsetWidth;
      el.classList.add('sm-cart-bump');
      setTimeout(function () { el.classList.remove('sm-cart-bump'); }, 550);
    });
  }

  document.addEventListener('cart:updated', bumpCartIcon);
}());

(function () {
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-action="toggle-discount"]');
    if (!trigger) return;
    e.preventDefault();

    var panel = document.getElementById('mini-cart-discount-panel');
    if (!panel) return;

    var willOpen = panel.hasAttribute('hidden');
    panel.toggleAttribute('hidden', !willOpen);
    trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    trigger.classList.toggle('is-open', willOpen);

    if (willOpen) {
      var input = panel.querySelector('.mini-cart__discount-input');
      if (input) setTimeout(function () { input.focus(); }, 60);
    }
  });
}());

/* iOS: Cart-Drawer ist position:fixed mit voller Layout-Viewport-Höhe. Beim
   Fokus aufs Rabattfeld verkleinert die Tastatur nur den visuellen Viewport –
   der Footer liegt dann hinter der Tastatur und die Seite blitzt durch. Wir
   begrenzen den Drawer über die VisualViewport-API auf den sichtbaren Bereich,
   solange ein Feld im Cart fokussiert ist. */
(function () {
  var vv = window.visualViewport;
  if (!vv) return;

  var engaged = false;

  function getCart() { return document.getElementById('mini-cart'); }

  function clear() {
    var d = getCart();
    if (!d) return;
    d.style.height = '';
    d.style.maxHeight = '';
    d.style.top = '';
  }

  function apply() {
    var d = getCart();
    if (!d) return;
    var keyboardOpen = vv.height < window.innerHeight - 100;
    if (keyboardOpen) {
      d.style.height = vv.height + 'px';
      d.style.maxHeight = vv.height + 'px';
      d.style.top = vv.offsetTop + 'px';
    } else {
      clear();
    }
  }

  function isCartField(t) {
    return t && t.closest && t.closest('#mini-cart') && t.matches && t.matches('input, textarea, select');
  }

  document.addEventListener('focusin', function (e) {
    if (!isCartField(e.target)) return;
    if (!engaged) {
      vv.addEventListener('resize', apply);
      vv.addEventListener('scroll', apply);
      engaged = true;
    }
    setTimeout(apply, 50);
  });

  document.addEventListener('focusout', function (e) {
    if (!isCartField(e.target)) return;
    setTimeout(function () {
      if (isCartField(document.activeElement)) return;
      if (engaged) {
        vv.removeEventListener('resize', apply);
        vv.removeEventListener('scroll', apply);
        engaged = false;
      }
      clear();
    }, 100);
  });
}());

/* /cart is not a second cart UI. The drawer opens on that URL; closing it
   leaves the page so the empty host is never the shopping cart. */
(function () {
  if (!document.body.classList.contains('template-cart')) return;
  if (window.themeVariables && window.themeVariables.cartType === 'page') return;

  var cart = document.getElementById('mini-cart');
  if (!cart) return;

  /* Do not put `open` in the Liquid tag. cart:refresh fetches /cart?section_id=
     and Focal parses a detached <cart-drawer open> that would lock every page. */
  if (!cart.open) cart.open = true;

  var leaving = false;
  function leaveCartPage() {
    if (leaving || cart.open || cart.hasAttribute('open')) return;
    leaving = true;
    if (window.history.length > 1) window.history.back();
    else window.location.assign(window.Shopify.routes.root);
  }

  new MutationObserver(function () {
    leaveCartPage();
  }).observe(cart, { attributes: true, attributeFilter: ['open'] });
}());

(function () {
  function formatAppCollectionRatings(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var badges = scope.querySelectorAll('.product-facet--app .jdgm-prev-badge');
    for (var i = 0; i < badges.length; i += 1) {
      var badge = badges[i];
      var widget = badge.closest('.jdgm-widget');
      var count = parseInt(badge.getAttribute('data-number-of-reviews') || '0', 10);
      if (!count) {
        if (widget) widget.setAttribute('hidden', '');
        continue;
      }
      if (widget) widget.removeAttribute('hidden');
      var raw = String(badge.getAttribute('data-average-rating') || '').replace(',', '.');
      var rating = parseFloat(raw);
      if (!isFinite(rating)) continue;
      badge.setAttribute('data-app-rating', rating.toFixed(1));
    }
  }

  function applyCollectionToolbarSync(root) {
    var sync = (root && root.querySelector && root.querySelector('#collection-toolbar-sync')) || document.getElementById('collection-toolbar-sync');
    var toolbar = document.getElementById('mobile-facet-toolbar');
    if (!toolbar || !sync) return;

    var sortLabel = document.getElementById('toolbar-sort-label') || toolbar.querySelector('.mobile-toolbar__item--sort .mobile-toolbar__item-label');
    var countEl = toolbar.querySelector('.mobile-toolbar__count');
    var filterBtn = toolbar.querySelector('.mobile-toolbar__item--filters');

    formatAppCollectionRatings(root);

    if (sortLabel && sync.dataset.sortLabel) {
      sortLabel.textContent = sync.dataset.sortLabel;
    }

    if (countEl && sync.dataset.countLabel) {
      countEl.textContent = sync.dataset.countLabel;
    }

    if (filterBtn) {
      var n = parseInt(sync.dataset.filterCount || '0', 10);
      if (isNaN(n)) n = 0;
      filterBtn.classList.toggle('has-filters', n > 0);
      var badge = filterBtn.querySelector('.mobile-toolbar__filter-count');
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'mobile-toolbar__filter-count';
          filterBtn.appendChild(badge);
        }
        badge.textContent = String(n);
      } else if (badge) {
        badge.remove();
      }
    }
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || input.name !== 'sort_by') return;
    var sortLabel = document.getElementById('toolbar-sort-label');
    var choice = input.closest('label');
    var choiceText = choice && choice.querySelector && choice.querySelector('.popover__choice-label');
    var text = (choiceText && choiceText.textContent) || input.getAttribute('title') || '';
    if (!text && input.nextElementSibling) text = input.nextElementSibling.textContent || '';
    if (sortLabel && text) sortLabel.textContent = text.trim();
  });

  document.addEventListener('facet:criteria-changed', function () {
    var main = document.getElementById('facet-main');
    if (!main) return;
    var observer = new MutationObserver(function () {
      applyCollectionToolbarSync(main);
    });
    observer.observe(main, { childList: true });
    setTimeout(function () {
      observer.disconnect();
      applyCollectionToolbarSync(main);
    }, 2000);
  });

  applyCollectionToolbarSync(document);
  formatAppCollectionRatings(document);
  document.addEventListener('jdgm.doneSetup', function () {
    formatAppCollectionRatings(document);
  });
}());

(function () {
  var startY = 0;
  var lastY = 0;
  var dragging = false;
  var pop = null;
  var sheet = null;

  function isSortSheet() {
    return window.matchMedia('(max-width: 999px)').matches && document.querySelector('.product-facet--app') && document.querySelector('.mobile-bottom-navigation');
  }

  document.addEventListener('touchstart', function (event) {
    if (!isSortSheet()) return;
    pop = document.getElementById('sort-by-popover');
    if (!pop || !pop.open) return;
    var target = event.target;
    if (!target.closest || !target.closest('#sort-by-popover')) return;
    if (target.closest('.popover__choice-item, .popover__close-button, .popover__overlay')) return;
    if (!target.closest('.popover__header')) return;
    sheet = pop.querySelector('.popover__sheet') || pop;
    startY = lastY = event.touches[0].clientY;
    dragging = true;
    sheet.style.transition = 'none';
  }, { passive: true });

  document.addEventListener('touchmove', function (event) {
    if (!dragging || !sheet) return;
    lastY = event.touches[0].clientY;
    sheet.style.transform = 'translateY(' + Math.max(0, lastY - startY) + 'px)';
  }, { passive: true });

  document.addEventListener('touchend', function () {
    if (!dragging || !pop) return;
    dragging = false;
    if (sheet) {
      sheet.style.transition = '';
      sheet.style.transform = '';
    }
    if (lastY - startY > 72) pop.open = false;
    pop = null;
    sheet = null;
  });
}());

/* Freeze the page behind drawers/modals. overflow:hidden on <html> alone
   still lets the homepage scroll (and shows its scrollbar) next to the menu. */
(function () {
  var html = document.documentElement;
  var locked = false;
  var scrollY = 0;

  function isLocked() {
    return html.classList.contains('lock-all') || html.classList.contains('lock-mobile');
  }

  function lock() {
    if (locked) return;
    var body = document.body;
    if (!body) return;

    locked = true;
    scrollY = window.scrollY || window.pageYOffset || 0;

    var compensate = 0;
    var header = document.querySelector('.shopify-section--header');
    var announcement = document.querySelector('.shopify-section--announcement-bar');
    var toolbar = document.getElementById('mobile-facet-toolbar');
    var pinHeader = true;

    if (announcement) {
      var announcementStyle = window.getComputedStyle(announcement);
      if (announcementStyle.display !== 'none') {
        pinHeader = announcement.getBoundingClientRect().bottom <= 0.5;
      }
    }

    if (pinHeader && header) compensate += header.offsetHeight || 0;
    if (toolbar) {
      var toolbarStyle = window.getComputedStyle(toolbar);
      if (toolbarStyle.display !== 'none' && toolbarStyle.visibility !== 'hidden') {
        compensate += toolbar.offsetHeight || 0;
      }
    }

    html.style.setProperty('--page-scroll-lock-gap', Math.max(0, window.innerWidth - html.clientWidth) + 'px');
    html.classList.add('page-scroll-locked');
    if (header) header.classList.toggle('is-scroll-lock-pinned', pinHeader);
    body.style.position = 'fixed';
    body.style.top = '-' + Math.max(0, scrollY - compensate) + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.paddingRight = 'var(--page-scroll-lock-gap, 0px)';
  }

  function unlock() {
    if (!locked) return;
    var body = document.body;

    locked = false;
    html.classList.remove('page-scroll-locked');
    html.style.removeProperty('--page-scroll-lock-gap');
    var header = document.querySelector('.shopify-section--header');
    if (header) header.classList.remove('is-scroll-lock-pinned');
    if (body) {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.paddingRight = '';
    }
    window.scrollTo(0, scrollY);
  }

  function sync() {
    if (isLocked()) lock();
    else unlock();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  new MutationObserver(sync).observe(html, {
    attributes: true,
    attributeFilter: ['class']
  });
}());